import { useOutletContext } from 'react-router-dom'

import {
  Box,
  CollapsableCard,
  RadioButton,
  Section,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  ProjectDetailsPageSettingsPageBudgetSectionFragment,
  ProjectManagementProjectDetailsPageSettingsPageFinancialDetailsSectionUpdateProjectDocument,
  ProjectManagementProjectDetailsPageSettingsPageFinancialDetailsSectionUpdateProjectMutation,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ProjectBudgetForm from 'modules/project_management/components/forms/ProjectBudgetForm'
import { IProjectBudgetValues } from 'modules/project_management/components/forms/ProjectBudgetStrategiesForm'
import { ProjectBudgetStrategiesForm } from 'modules/project_management/components/forms/ProjectBudgetStrategiesForm'
import {
  useCreateBudgetStrategy,
  useDeleteBudgetStrategy,
  useUpdateBudgetStrategy,
} from 'modules/project_management/hooks/planningMutations/budgetStrategyHooks'
import { budgetTypesEnum } from 'modules/project_management/lib/constants'
import { getDecimalHoursFromMinutes } from 'modules/project_management/lib/formatHours'
import { isValidBudgetType } from 'modules/project_management/lib/utils'

import { getBudgetSectionMutation } from './getBudgetSectionMutation'
import { getBudgetSectionMutationOld } from './getBudgetSectionMutationOld'

export const FixedBudgetSection: React.FC<{
  project: ProjectDetailsPageSettingsPageBudgetSectionFragment
  hasSubprojectsFeature: boolean
}> = ({ project, hasSubprojectsFeature }) => {
  const featureAvailable = hasSubprojectsFeature

  const { company } = useCurrentSession()

  const { createBudgetStrategy } = useCreateBudgetStrategy(company.id)
  const { updateBudgetStrategy } = useUpdateBudgetStrategy()
  const { deleteBudgetStrategy } = useDeleteBudgetStrategy()

  const [updateFinancialDetailsProject] = useMutation(
    ProjectManagementProjectDetailsPageSettingsPageFinancialDetailsSectionUpdateProjectDocument,
    {
      extractorFn: (
        data: ProjectManagementProjectDetailsPageSettingsPageFinancialDetailsSectionUpdateProjectMutation
      ) => data.projectManagementMutations.updateFinancialDetailsProjectmanagementProject,
    }
  )

  const originalBudgetStrategy = project.budgetStrategy

  // Helper to get current budget type
  const getCurrentBudgetType = (): budgetTypesEnum => {
    if (project.budgetStrategy && isValidBudgetType(project.budgetStrategy.budgetType)) {
      return project.budgetStrategy.budgetType
    }
    return budgetTypesEnum.withoutBudget
  }

  const getMutation = (data: IProjectBudgetValues) =>
    hasSubprojectsFeature
      ? getBudgetSectionMutation({
          project,
          data,
          getCurrentBudgetType,
          createBudgetStrategy,
          updateBudgetStrategy,
          deleteBudgetStrategy,
          updateFinancialDetailsProject,
          hasSubprojectsFeature,
        })
      : getBudgetSectionMutationOld({
          project,
          data,
          getCurrentBudgetType,
          createBudgetStrategy,
          updateBudgetStrategy,
          deleteBudgetStrategy,
        })

  const { hasCost: hasCostsFeature } = useOutletContext<{
    hasCost: boolean
  }>()

  const getProjectBudgetDefaultValues = () => {
    const currentBudgetType = getCurrentBudgetType()
    const baseCurrency = { currencyType: project.legalEntity.currency }

    const baseValues = {
      fixedCostCents: project.fixedCostCents ? project.fixedCostCents / 100 : undefined,
      isBillable: project.isBillable ?? false,
      ...baseCurrency,
    }

    const budgetStrategy = project.budgetStrategy

    if (!budgetStrategy || currentBudgetType === budgetTypesEnum.withoutBudget) {
      return {
        budgetType: budgetTypesEnum.withoutBudget,
        feeAmountCents: undefined,
        plannedCents: undefined,
        plannedMinutes: undefined,
        delegated: project.budgetStrategy?.delegated ?? false,
        ...baseValues,
      }
    }

    return {
      budgetType: currentBudgetType,
      budgetAllocationCents: null,
      feeAmountCents: budgetStrategy.feeAmountCents
        ? budgetStrategy.feeAmountCents / 100
        : undefined,
      plannedCents: budgetStrategy.plannedCents ? budgetStrategy.plannedCents / 100 : undefined,
      plannedMinutes: budgetStrategy.plannedMinutes
        ? getDecimalHoursFromMinutes(budgetStrategy.plannedMinutes)
        : undefined,
      delegated: budgetStrategy.delegated ?? false,
      ...baseValues,
    }
  }

  const { values, errors, setValues, setErrors, handleSubmit, dirty, resetForm, isSubmitting } =
    useForm<IProjectBudgetValues>({
      defaultValues: getProjectBudgetDefaultValues(),
      validateOnChange: true,
      validateOnBlur: true,
      validate: (data) => {
        const errorValidation: FormErrors<IProjectBudgetValues> = {}
        const isProjectLevel = !data.delegated

        if (!isProjectLevel) {
          return errorValidation
        }

        if (data.budgetType !== budgetTypesEnum.withoutBudget) {
          if (data.plannedCents !== undefined && data.plannedCents !== null) {
            if (isNaN(data.plannedCents)) {
              errorValidation.plannedCents = i18n.t(
                'project_management.forms.financial_details.fixed_costs.format_error'
              )
            } else if (data.plannedCents < 0) {
              errorValidation.plannedCents = i18n.t(
                'project_management.forms.financial_details.fixed_costs.negative_error'
              )
            }
          }
        }

        if (data.budgetType !== budgetTypesEnum.withoutBudget && data.isBillable) {
          if (data.feeAmountCents !== undefined && data.feeAmountCents !== null) {
            if (isNaN(data.feeAmountCents)) {
              errorValidation.feeAmountCents = i18n.t(
                'project_management.planning.validation_errors.field_is_number'
              )
            } else if (data.feeAmountCents < 0) {
              errorValidation.feeAmountCents = i18n.t(
                'project_management.forms.financial_details.fixed_costs.negative_error'
              )
            }
          }
        }

        if (data.budgetType === budgetTypesEnum.totalBudget) {
          if (data.plannedMinutes !== undefined && data.plannedMinutes !== null) {
            if (isNaN(data.plannedMinutes)) {
              errorValidation.plannedMinutes = i18n.t(
                'project_management.forms.financial_details.fixed_costs.format_error'
              )
            } else if (data.plannedMinutes < 0) {
              errorValidation.plannedMinutes = i18n.t(
                'project_management.forms.financial_details.fixed_costs.negative_error'
              )
            }
          }
        }

        if (data.fixedCostCents !== undefined && data.fixedCostCents !== null) {
          if (isNaN(data.fixedCostCents)) {
            errorValidation.fixedCostCents = i18n.t(
              'project_management.forms.financial_details.fixed_costs.format_error'
            )
          } else if (data.fixedCostCents < 0) {
            errorValidation.fixedCostCents = i18n.t(
              'project_management.forms.financial_details.fixed_costs.negative_error'
            )
          }
        }

        return errorValidation
      },
      onSubmit: async (data) => {
        const result = await getMutation(data)
        const handled = handleResult(result, (err) => {
          if (err) setErrors(err)
        })

        if (handled.success) {
          const trackBudgetAction = () => {
            const currentBudgetType = getCurrentBudgetType()
            const targetBudgetType = data.budgetType

            // Handle deletion case
            if (targetBudgetType === budgetTypesEnum.withoutBudget) {
              if (currentBudgetType !== budgetTypesEnum.withoutBudget) {
                track(trackingEvents.projectManagement.settings.deleteBudget)
              }
              return
            }

            // Get tracking budget type name
            const getTrackingBudgetType = (budgetType: string) => {
              switch (budgetType) {
                case budgetTypesEnum.projectFixedCost:
                  return project.isBillable ? 'Planned_cost_income' : 'Planned_cost'
                case budgetTypesEnum.totalBudget:
                  return 'total budget'
                case budgetTypesEnum.withoutBudget:
                  return 'no budget'
                default:
                  return budgetType
              }
            }

            const trackingBudgetType = getTrackingBudgetType(targetBudgetType)
            const isUpdate = currentBudgetType === targetBudgetType
            const isCreate = currentBudgetType === budgetTypesEnum.withoutBudget

            // Determine tracking parameters
            const trackingParams: Record<string, string> = {
              budgetType: trackingBudgetType,
            }

            // Add specific parameters for fixed cost planning
            if (targetBudgetType === budgetTypesEnum.projectFixedCost) {
              trackingParams.income = data.feeAmountCents ? 'yes' : 'no'

              // Add amountChanged for updates
              if (
                isUpdate &&
                project.budgetStrategy &&
                project.budgetStrategy.plannedCents &&
                project.budgetStrategy.budgetType === budgetTypesEnum.projectFixedCost
              ) {
                trackingParams.amountChanged =
                  data.plannedCents !== project.budgetStrategy.plannedCents / 100 ? 'yes' : 'no'
              } else if (!isCreate) {
                trackingParams.amountChanged = 'yes'
              }
            } else if (isUpdate && !isCreate) {
              // For other budget types, always mark amount as changed when updating
              trackingParams.amountChanged = 'yes'
            }

            trackingParams.billable = data.isBillable.toString()
            trackingParams.delegation = data.delegated ? 'Phase' : 'Project'

            // Track the appropriate event
            if (isCreate) {
              track(trackingEvents.projectManagement.settings.addBudget, trackingParams)
            } else {
              track(trackingEvents.projectManagement.settings.updateBudget, trackingParams)
            }
          }

          trackBudgetAction()

          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
          resetForm({ values: data })
        }

        return handled
      },
    })
  const handleFieldChanged = (
    fieldName: string,
    fieldValue: number | string | boolean | undefined,
    shouldValidate = false
  ) => {
    const numericFields = ['plannedCents', 'plannedMinutes', 'feeAmountCents', 'fixedCostCents']
    let normalizedValue = fieldValue
    if (numericFields.includes(fieldName) && typeof fieldValue === 'string') {
      normalizedValue = fieldValue === '' ? undefined : Number(fieldValue)
    }

    const newValues: IProjectBudgetValues = { ...values, [fieldName]: normalizedValue }

    if (fieldName === 'budgetType') {
      setErrors({})
    }

    setValues(newValues, shouldValidate)
  }

  return (
    <>
      {featureAvailable ? (
        <Section
          illustration="money"
          title={i18n.t('project_management.settings.sections.budget_strategies_section.title')}
          description={i18n.t(
            'project_management.settings.sections.budget_strategies_section.description'
          )}
          paddingX="s0"
        >
          <Box gap="s24">
            <Box
              style={{ zIndex: values.budgetType === budgetTypesEnum.totalBudget ? '9999' : '1' }}
            >
              <CollapsableCard
                open={values.budgetType === budgetTypesEnum.totalBudget}
                Card={
                  <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
                    <Box flexDirection="column" gap="s4">
                      <Text.Normal size="300" weight="bold">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.time_and_material.title'
                        )}
                      </Text.Normal>
                      <Text.Normal size="200">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.time_and_material.subtitle'
                        )}
                      </Text.Normal>
                    </Box>
                    <RadioButton
                      value="timeAndMaterial"
                      checked={values.budgetType === budgetTypesEnum.totalBudget}
                      onChange={() => handleFieldChanged('budgetType', budgetTypesEnum.totalBudget)}
                    />
                  </Box>
                }
                Content={
                  values.budgetType === budgetTypesEnum.totalBudget ? (
                    <ProjectBudgetStrategiesForm
                      projectId={project.id}
                      originalBudgetStrategy={originalBudgetStrategy}
                      onChangeValue={handleFieldChanged}
                      strategyType={budgetTypesEnum.totalBudget}
                      values={values}
                      errors={errors}
                      isOwner={!!project._policies?.assignOwner.can}
                      hasCostsFeature={hasCostsFeature}
                      hasBudgetFeature
                      fixedCostCents={values.fixedCostCents}
                      shouldShowSaveButton={dirty || isSubmitting}
                      isSubmitting={isSubmitting}
                      onSubmit={handleSubmit}
                    />
                  ) : null
                }
              />
            </Box>
            <Box
              style={{
                zIndex: values.budgetType === budgetTypesEnum.projectFixedCost ? '9999' : '1',
              }}
            >
              <CollapsableCard
                open={values.budgetType === budgetTypesEnum.projectFixedCost}
                Card={
                  <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
                    <Box flexDirection="column" gap="s4">
                      <Text.Normal size="300" weight="bold">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.fixed_price.title'
                        )}
                      </Text.Normal>
                      <Text.Normal size="200">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.fixed_price.subtitle'
                        )}
                      </Text.Normal>
                    </Box>
                    <RadioButton
                      value="fixedPrice"
                      checked={values.budgetType === budgetTypesEnum.projectFixedCost}
                      onChange={() =>
                        handleFieldChanged('budgetType', budgetTypesEnum.projectFixedCost)
                      }
                    />
                  </Box>
                }
                Content={
                  values.budgetType === budgetTypesEnum.projectFixedCost ? (
                    <ProjectBudgetStrategiesForm
                      projectId={project.id}
                      originalBudgetStrategy={originalBudgetStrategy}
                      onChangeValue={handleFieldChanged}
                      strategyType={budgetTypesEnum.projectFixedCost}
                      values={values}
                      errors={errors}
                      isOwner={!!project._policies?.assignOwner.can}
                      hasCostsFeature={hasCostsFeature}
                      hasBudgetFeature
                      fixedCostCents={values.fixedCostCents}
                      shouldShowSaveButton={dirty || isSubmitting}
                      isSubmitting={isSubmitting}
                      onSubmit={handleSubmit}
                    />
                  ) : null
                }
              />
            </Box>
            <Box
              style={{ zIndex: values.budgetType === budgetTypesEnum.withoutBudget ? '9999' : '1' }}
            >
              <CollapsableCard
                open={values.budgetType === budgetTypesEnum.withoutBudget}
                Card={
                  <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
                    <Box flexDirection="column" gap="s4">
                      <Text.Normal size="300" weight="bold">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.no_budget.title'
                        )}
                      </Text.Normal>
                      <Text.Normal size="200">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.no_budget.subtitle'
                        )}
                      </Text.Normal>
                    </Box>
                    <RadioButton
                      value="noBudget"
                      checked={values.budgetType === budgetTypesEnum.withoutBudget}
                      onChange={() =>
                        handleFieldChanged('budgetType', budgetTypesEnum.withoutBudget)
                      }
                    />
                  </Box>
                }
                Content={
                  values.budgetType === budgetTypesEnum.withoutBudget ? (
                    <ProjectBudgetStrategiesForm
                      projectId={project.id}
                      originalBudgetStrategy={originalBudgetStrategy}
                      onChangeValue={handleFieldChanged}
                      strategyType={budgetTypesEnum.withoutBudget}
                      values={values}
                      errors={errors}
                      isOwner={!!project._policies?.assignOwner.can}
                      hasCostsFeature={hasCostsFeature}
                      hasBudgetFeature
                      fixedCostCents={values.fixedCostCents}
                      shouldShowSaveButton={dirty || isSubmitting}
                      isSubmitting={isSubmitting}
                      onSubmit={handleSubmit}
                    />
                  ) : null
                }
              />
            </Box>
          </Box>
        </Section>
      ) : (
        <Section.Form
          illustration="money"
          title={i18n.t('project_management.planning.title')}
          description={i18n.t('project_management.planning.description')}
          onSubmit={handleSubmit}
          submitLabel={isSubmitting ? i18n.t('common.saving') : i18n.t('common.submit')}
          disabled={isSubmitting}
          isDirty={dirty || isSubmitting}
          paddingX="s0"
        >
          <ProjectBudgetForm
            onChangeValue={handleFieldChanged}
            values={values}
            errors={errors}
            isBillable={project.isBillable ?? false}
            isOwner={!!project._policies?.assignOwner.can}
            hasCostsFeature={hasCostsFeature}
            hasBudgetFeature
          />
        </Section.Form>
      )}
    </>
  )
}
